import { useMemo } from 'react';
import isEmpty from 'lodash.isempty';

import type { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as Variant } from '../../hook/__generated__/PricingPageShopProduct';
import type { UpgradeVariants } from '../../hook/useUpgradePaths';
import { LicenseCardVariant } from '../LicenseCard/LicenseCard.constants';

interface useCardVariantProps {
  variants: (Variant | null)[];
  upgradeVariants: UpgradeVariants;
  variantsWithRequiredBaseProducts?: string[];
  isAuthenticated?: boolean;
}

const useCardVariant = ({
  variants,
  upgradeVariants,
  variantsWithRequiredBaseProducts,
  isAuthenticated,
}: useCardVariantProps) => {
  const isSingleLicenseProduct = variants?.length === 1;
  const upgradeVariantsKeys = Object.keys(upgradeVariants);

  const getBestOptionVariantSku = useMemo(() => {
    const isSdbsIdInUpgradeVariants = (variant): boolean =>
      variant?.product?.sdbsId &&
      upgradeVariantsKeys.includes(variant?.product?.sdbsId);

    const upgradeableVariants = variants?.filter(isSdbsIdInUpgradeVariants);

    if (isEmpty(upgradeableVariants)) {
      return variants?.[0]?.product?.sku;
    }

    const orderVariants = upgradeableVariants.sort((a, b) => {
      const priceA =
        a?.product?.priceRange?.minimumPrice?.finalPrice?.value || 0;
      const priceB =
        b?.product?.priceRange?.minimumPrice?.finalPrice?.value || 0;

      return priceA - priceB;
    })[0];

    return orderVariants?.product?.sku;
  }, [variants, upgradeVariantsKeys]);

  const getCardVariant = (variant: Variant) => {
    const variantHasUpgrades = upgradeVariantsKeys.includes(
      variant?.product?.sdbsId || '',
    );

    if (!isAuthenticated || isSingleLicenseProduct) {
      return LicenseCardVariant.Default;
    }

    if (variant?.product?.sku === getBestOptionVariantSku) {
      return LicenseCardVariant.Recommendation;
    }

    if (
      !variantHasUpgrades &&
      variantsWithRequiredBaseProducts?.includes(variant?.product?.sku || '')
    ) {
      return LicenseCardVariant.Unqualified;
    }

    return LicenseCardVariant.Default;
  };

  return { getCardVariant };
};

export default useCardVariant;
